import { QueryClient, MutationCache } from '@tanstack/react-query';
import { toast } from '@mlm/ui';
import { apiErrorMessage, apiErrorPayload } from './api';

/**
 * Typed mutation `meta`. A mutation opts into a success toast by declaring
 * `meta: { successMessage: 'Saved.' }`; `errorMessage` overrides the default
 * server-derived error toast for that mutation.
 */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      successMessage?: string;
      errorMessage?: string;
      /**
       * Server error codes this mutation handles itself (its `onError` opens a
       * confirm dialog, say). Listed codes skip the default error toast so the
       * admin doesn't get a raw `sku_in_use` alongside the dialog explaining it.
       */
      silentErrorCodes?: string[];
    };
  }
}

/**
 * Single shared QueryClient instance. Lives outside main.tsx so the api
 * layer can call `queryClient.clear()` on logout / auth loss — otherwise
 * stale data from the previous user leaks into the next session and the
 * user has to Ctrl+F5 to see fresh data.
 *
 * The MutationCache gives every save/mutation feedback for free: any failure
 * toasts the server's error message (no silent failures anywhere), and a
 * mutation shows a success toast when it sets `meta.successMessage`.
 */
export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, gcTime: 5 * 60_000, retry: 1 } },
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      const code = apiErrorPayload(error)?.error;
      if (code && mutation.meta?.silentErrorCodes?.includes(code)) return;
      toast.error(
        mutation.meta?.errorMessage ??
          apiErrorMessage(error, 'Something went wrong. Please try again.'),
      );
    },
    onSuccess: (data, _vars, _ctx, mutation) => {
      // A route may return `warning` for "saved, but you should know something"
      // — a state the save itself can't reject because the fix lives elsewhere.
      // It REPLACES the success toast: "Page updated." on top of it would bury
      // the only sentence worth reading. Shown as `info`, not `error`, because
      // the write did succeed; longer-lived because it's a sentence, not a tick.
      const warning = (data as { warning?: unknown } | null | undefined)?.warning;
      if (typeof warning === 'string' && warning) {
        toast.info(warning, 9000);
        return;
      }
      if (mutation.meta?.successMessage) toast.success(mutation.meta.successMessage);
    },
  }),
});
